import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import {
  getCourse,
  listCourseFilters,
  listCourses,
  listUpcomingCourseSessions,
} from '@/js/core/utils/courses-api';

// Compares queries ignoring key order and empty values, so `{}` and
// `{alphabet: [], ...}` count as the same (unfiltered) query.
function queryKey(query) {
  const entries = Object.entries(query || {})
    .filter(([, value]) => {
      if (value === null || value === undefined || value === '') return false;

      return !(Array.isArray(value) && value.length === 0);
    })
    .sort(([a], [b]) => a.localeCompare(b));

  return JSON.stringify(entries);
}

// A paged, query-keyed collection: page 1 replaces, later pages append.
// Refetches only when the query changes or force is set.
function createPagedList(requestFn, errorText) {
  const items = ref([]);
  const pagination = ref(null);
  const query = ref({});
  const isLoading = ref(false);
  const error = ref('');
  let fetchId = 0;

  const hasMore = computed(() =>
    Boolean(pagination.value && pagination.value.current_page < pagination.value.last_page)
  );

  async function fetch({ page = 1, query: nextQuery = null, force = false } = {}) {
    const resolvedQuery = nextQuery ?? query.value;
    const sameQuery = queryKey(resolvedQuery) === queryKey(query.value);

    if (!force && page === 1 && sameQuery && items.value.length > 0) return items.value;

    isLoading.value = true;
    error.value = '';
    const currentFetchId = ++fetchId;

    try {
      const response = await requestFn({ ...resolvedQuery, page });

      if (currentFetchId !== fetchId) return items.value;

      const pageItems = response?.data || [];

      items.value = page > 1 ? [...items.value, ...pageItems] : pageItems;
      pagination.value = response?.meta?.pagination || null;
      query.value = resolvedQuery;

      return items.value;
    } catch (err) {
      if (currentFetchId !== fetchId) return items.value;

      error.value = err.message || errorText;

      return items.value;
    } finally {
      if (currentFetchId === fetchId) {
        isLoading.value = false;
      }
    }
  }

  async function fetchMore() {
    if (!hasMore.value || isLoading.value) return items.value;

    return fetch({ page: (pagination.value?.current_page || 1) + 1 });
  }

  return { items, pagination, query, isLoading, error, hasMore, fetch, fetchMore };
}

export const useCoursesStore = defineStore('courses', () => {
  const coursesList = createPagedList(listCourses, 'Could not load courses.');
  const upcomingList = createPagedList(
    listUpcomingCourseSessions,
    'Could not load upcoming course sessions.'
  );
  const filters = ref(null);
  const courseDetails = ref({});
  const isLoadingFilters = ref(false);
  const isLoadingCourse = ref(false);
  const filtersError = ref('');
  const courseError = ref('');
  const courseErrorDetails = ref(null);
  const detailRequests = new Map();

  const fetchCourses = coursesList.fetch;
  const fetchMoreCourses = coursesList.fetchMore;
  const fetchUpcoming = upcomingList.fetch;
  const fetchMoreUpcoming = upcomingList.fetchMore;

  async function fetchFilters({ force = false } = {}) {
    if (!force && filters.value) return filters.value;

    isLoadingFilters.value = true;
    filtersError.value = '';

    try {
      const response = await listCourseFilters();

      filters.value = response?.data || null;

      return filters.value;
    } catch (err) {
      filtersError.value = err.message || 'Could not load course filters.';

      return filters.value;
    } finally {
      isLoadingFilters.value = false;
    }
  }

  async function fetchCourse(courseSlug, { force = false } = {}) {
    const slug = String(courseSlug || '');

    if (!slug) return null;
    courseError.value = '';
    courseErrorDetails.value = null;
    if (!force && courseDetails.value[slug]) return courseDetails.value[slug];
    if (detailRequests.has(slug)) return detailRequests.get(slug);

    isLoadingCourse.value = true;

    const request = getCourse(slug)
      .then(response => {
        const course = response?.data || null;

        if (course) {
          courseDetails.value = {
            ...courseDetails.value,
            [slug]: course,
          };
        }

        return course;
      })
      .catch(err => {
        courseError.value = err.message || 'Could not load course.';
        courseErrorDetails.value = err;

        return null;
      })
      .finally(() => {
        detailRequests.delete(slug);
        isLoadingCourse.value = detailRequests.size > 0;
      });

    detailRequests.set(slug, request);

    return request;
  }

  async function preloadCoursesPage({ includeUpcoming = false, query = null } = {}) {
    const requests = [fetchFilters(), fetchCourses({ query })];

    if (includeUpcoming) {
      requests.push(fetchUpcoming({ query }));
    }

    await Promise.all(requests);
  }

  async function preloadCourseDetail(courseSlug) {
    return fetchCourse(courseSlug);
  }

  function courseBySlug(courseSlug) {
    return courseDetails.value[String(courseSlug || '')] || null;
  }

  return {
    courses: coursesList.items,
    coursesPagination: coursesList.pagination,
    coursesQuery: coursesList.query,
    hasMoreCourses: coursesList.hasMore,
    upcomingSessions: upcomingList.items,
    upcomingPagination: upcomingList.pagination,
    upcomingQuery: upcomingList.query,
    hasMoreUpcoming: upcomingList.hasMore,
    filters,
    courseDetails,
    isLoadingCourses: coursesList.isLoading,
    isLoadingUpcoming: upcomingList.isLoading,
    isLoadingFilters,
    isLoadingCourse,
    coursesError: coursesList.error,
    upcomingError: upcomingList.error,
    filtersError,
    courseError,
    courseErrorDetails,
    fetchCourses,
    fetchMoreCourses,
    fetchUpcoming,
    fetchMoreUpcoming,
    preloadCoursesPage,
    preloadCourseDetail,
    courseBySlug,
  };
});
